import * as logger from 'logger';
import * as tenantService from 'services/tenant-service';
import * as userApi from 'api/user-api';
import {
  UserManagementErrorAction,
  GetUserAction,
  LoadUsersListAction
} from 'pages/user-management/actions';
import { Middleware, MiddlewareAPI } from 'redux';
import {
  EDIT_ROW,
  EditRowAction,
  LIST_ROWS,
  ListRowsAction,
  SetColumnsAction,
  SetRowsAction
} from 'components/user-grid/actions';
import { SAVE_TENANT } from '../settings/actions';
import { getI18n, getRoleLabel } from 'services/i18n-service';
import { getUserPermissions } from 'services/token-service';
import { GridLayoutType } from 'components/user-grid/state';
import { hasPermissionTo } from 'helpers/permission-helper';
import { Permission } from 'types/permission';
import { Mode } from 'types/mode';
import { translations } from 'types/translations';

const UsersMiddleware: Middleware = store => next => async action => {

  try {
    switch (action.type) {
      case LIST_ROWS:
        await listRows(store, action);
        break;

      case EDIT_ROW:
        try {
          await editRow(store, action);
        }
        catch (error) {
          logger.error(error, 'UsersMiddleware.EDIT_ROW');
          store.dispatch(new UserManagementErrorAction(error.message));
        }
        break;

      case SAVE_TENANT:
        translateColumns(store);
        break;

      default:
        break;
    }

    next(JSON.parse(JSON.stringify(action)));
  }
  catch (e) {
    const error = e as Error;
    logger.error(error, 'UsersMiddleware');
  }
};

async function listRows(store: MiddlewareAPI, action: ListRowsAction): Promise<void> {
  try {
    store.dispatch(new LoadUsersListAction());

    translateColumns(store);

    const tenantId = await tenantService.getTenantId();
    const response = await userApi.listUsers(tenantId);

    for (const user of response.users) {
      if (user) {
        user.fullName = user.firstName.toLowerCase() + ' ' + user.lastName.toLowerCase();
        // used for ag-grid fitlering
        user.roleLabel = getRoleLabel(user.roleType);
      }
    }

    store.dispatch(new SetRowsAction(response.users));
  }
  catch (error) {
    logger.error(error, 'UsersMiddleware.listRows');
  }
}

async function editRow(store: MiddlewareAPI, action: EditRowAction): Promise<void> {
  store.dispatch(new GetUserAction(action.rowId));
}

async function translateColumns(store: MiddlewareAPI): Promise<void> {
  const i18n = getI18n();

  const permissions: string = await getUserPermissions();
  const canEditUser: boolean =
    hasPermissionTo([Mode.Update, Mode.Delete], Permission.Users, permissions);

  const desktopColumns = [
    { field: 'id', hide: true },
    {
      type: canEditUser ? 'linkColumn' : 'fullnameColumn', width: 35, headerName: i18n.t(translations.FullName),
      cellClass: 'left-content-cell cell-class', headerClass: 'left-content-header',
      sort: 'asc', field: 'fullName', sortedAt: 1
    },
    { field: 'roleLabel', type: 'textColumn', width: 25, headerName: i18n.t(translations.Role) },
    { field: 'isActive', type: 'checkColumn', filter: 'booleanFilter', width: 25, headerName: i18n.t(translations.Active),
      sort: 'desc', sortedAt: 0
    }
  ];
  store.dispatch(new SetColumnsAction(GridLayoutType.usersComputer, desktopColumns));

  const mobileColumns = [
    { field: 'id', hide: true },
    {
      type: canEditUser ? 'linkColumn' : 'fullnameColumn', width: 50, headerName: i18n.t(translations.UserDetails),
      cellClass: 'left-content-cell cell-class', headerClass: 'left-content-header',
      sort: 'asc', field: 'fullName', sortedAt: 1
    },
    { field: 'isActive', type: 'checkColumn', width: 20, headerName: i18n.t(translations.Active),
      sort: 'desc', sortedAt: 0, headerClass: 'center-content-header', cellClass: 'center-content-cell cell-class'
    }
  ];
  store.dispatch(new SetColumnsAction(GridLayoutType.usersMobile, mobileColumns));
}

export default UsersMiddleware;


// WEBPACK FOOTER //
// ./src/pages/users/middleware.ts